import type { Fnode } from "@protontech/fathom";
import { utils } from "@protontech/fathom";

import { MIN_FIELD_HEIGHT, MIN_FIELD_WIDTH } from "@protontech/autofill/constants/heuristics";
import { inputCandidateSelector } from "@protontech/autofill/constants/selectors";
import { any } from "@protontech/autofill/utils/combinators";
import { flagAsHidden } from "@protontech/autofill/utils/flags";
import { isIFrameField } from "@protontech/autofill/utils/iframe";
import { matchHidden } from "@protontech/autofill/utils/re";
import { isCustomElementWithShadowRoot, isShadowElement } from "@protontech/autofill/utils/shadow-dom";
import { sanitizeStringWithSpaces } from "@protontech/autofill/utils/text";
import { type QuickCheckOptions, quickCache, visibilityCache } from "./cache";
import { clearViewportCache, getViewport } from "./viewport";
import { VisibilityWalk } from "./walk";

type IsVisibleOptions = { opacity: boolean; skipCache?: boolean };

/** Reset every memoised visibility verdict. Must run once per detection pass so
 * verdicts never leak between runs (see `clearDetectionCache`). */
export const clearVisibilityCache = () => {
  visibilityCache.clear();
  quickCache.clear();
  clearViewportCache();
};

/* Heuristic visibility: walk the shadow-piercing ancestor chain to <body>, hiding on any
 * ancestor's style (opacity <0.1, visibility, display, clip) or geometry (clipped or
 * scrolled out of reach), else visible if the root scroller can bring it on screen.
 * Overflow is handled per-axis; absolute elements skip static ancestors, fixed check the
 * viewport, boxless ancestors ground on the nearest box. `VisibilityWalk` (see walk.ts)
 * holds the state and the cascade; a hidden verdict is cached only for the chain it covers. */
export const isVisible = (fnodeOrElement: Fnode | HTMLElement, options: IsVisibleOptions): boolean => {
  const useCache = !options.skipCache;

  const element = utils.toDomElement(fnodeOrElement);
  const cache = visibilityCache.read(element, options.opacity);
  if (useCache && cache !== undefined) return cache;

  const win = utils.windowForElement(element);
  const doc = win.document;
  const viewport = getViewport(win, doc);

  const walk = new VisibilityWalk({ element, win, doc, viewport, opacity: options.opacity, useCache });
  const visible = walk.evaluate();

  /* Cache the verdict over the covered chain (already trimmed to the nodes it covers).
   * `hiddenByOpacity` flags an opacity-only hide, kept out of the opacity-independent table. */
  if (useCache) visibilityCache.writeChain(walk.coverage, visible, options.opacity, walk.hiddenByOpacity);

  return visible;
};

/* quick visibility check with heuristics with memoisation. Not as precise as the
 * more in-depth `isVisible` check which will check ancestors. Applies heuristics
 * for hidden classname detection and minimum dimensions */
const quickVisibilityCheck = (el: HTMLElement, options: QuickCheckOptions) => {
  const cache = quickCache.get(el, options);
  if (cache !== undefined) return cache;

  const check = (): boolean => {
    /* no need to check further if no client rects */
    const rect = el.getClientRects();
    if (rect.length === 0) return false;

    /* heuristics for class names */
    const classList = Array.from(el.classList).map(sanitizeStringWithSpaces);
    if (any(matchHidden)(classList)) return false;

    /* css heuristics - excluding opacity */
    const { visibility, display, maxHeight } = getComputedStyle(el);
    if (visibility === "hidden" || display === "none" || maxHeight === "0px") return false;

    /* dimension heuristics */
    if (el.offsetHeight === 0 || el.offsetWidth === 0) return false;
    if (el.offsetHeight < options.minHeight || el.offsetWidth < options.minWidth) return false;

    return true;
  };

  const visible = check();
  quickCache.set(el, options, visible);

  return visible;
};

export const isVisibleEl = (el: HTMLElement) => quickVisibilityCheck(el, { minHeight: 0, minWidth: 0 });

/** A form element is considered visible if at least one of its
 * fields is visible. Checking only the form element itself may be
 * insufficient as we may hit false positives on certain websites
 * that hide only the form field elements while keeping the form
 * container visible but inactive. */
export const isVisibleForm = (form: HTMLElement, options: Partial<Pick<IsVisibleOptions, "skipCache">> = {}) => {
  const visible = (() => {
    if (!isVisible(form, { opacity: true, ...options })) return false;

    /** Custom elements with shadow roots or shadow elements may have input fields
     * nested in shadow DOM, making input visibility heuristics unreliable.
     * FIXME: support non-shallow shadow DOM queries to preserve heuristic */
    if (isCustomElementWithShadowRoot(form) || isShadowElement(form)) return true;

    /** Check that at least one input field is visible */
    const inputs = Array.from(form.querySelectorAll<HTMLInputElement>(inputCandidateSelector)).filter((field) => !field.disabled);
    const iframes = Array.from(form.querySelectorAll("iframe")).filter(isIFrameField);
    const fields: HTMLElement[] = [...inputs, ...iframes];

    return fields.length > 0 && fields.some((input) => isVisible(input, { opacity: false, ...options }));
  })();

  if (!visible) flagAsHidden(form);
  return visible;
};

export const isVisibleField = (field: HTMLElement) => {
  if (field instanceof HTMLInputElement) {
    const { type, disabled, readOnly } = field;
    if (type === "hidden" || disabled || readOnly || field.getAttribute("aria-hidden") === "true") return false;
  }

  return quickVisibilityCheck(field, { minHeight: MIN_FIELD_HEIGHT, minWidth: MIN_FIELD_WIDTH });
};
